import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './FounderStory.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import apiService from '../services/api';

// --- Reusable Components ---

function Reveal({ as: Tag = 'div', className = '', delay = 0, children, ...rest }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref]);
  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? 'reveal-visible' : ''} ${className}`}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

// --- Safe JSON Parser ---
const parseBooksData = (booksData) => {
  // If already an array, return it
  if (Array.isArray(booksData)) return booksData;
  
  // If null or undefined, return empty array
  if (!booksData) return [];
  
  // If it's a string, try to parse it
  if (typeof booksData === 'string') {
    try {
      const parsed = JSON.parse(booksData);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.error('❌ Error parsing books JSON:', e);
      return [];
    }
  }
  
  return [];
};

// --- Safe HTML Renderer ---
const SafeHTML = ({ html, className = '' }) => {
  if (!html) return null;
  return <div className={className} dangerouslySetInnerHTML={{ __html: html }} />;
};

// --- Main FounderStory Component ---

export default function FounderStory() {
  const [founder, setFounder] = useState(null);
  const [awards, setAwards] = useState([]);
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch founder data
  useEffect(() => {
    const fetchFounder = async () => {
      try {
        setLoading(true);
        const result = await apiService.fetchAboutFounder();
        
        console.log('📖 Founder response:', result);
        
        // Check if we have valid data
        if (result?.status === 'success' && result?.data && result.data.length > 0) {
          const founderData = result.data[0];
          setFounder(founderData);
          
          // Parse books JSON
          const booksData = parseBooksData(founderData.books);
          setBooks(booksData);
          
          console.log('📚 Parsed books:', booksData);
        } else {
          setError('No founder data available');
        }
      } catch (err) {
        console.error('❌ Error fetching founder:', err);
        setError(err.message || 'Failed to load founder data');
      } finally {
        setLoading(false);
      }
    };

    fetchFounder();
  }, []);

  // Fetch awards data
  useEffect(() => {
    const fetchAwards = async () => {
      try {
        const result = await apiService.fetchAwards();
        console.log('🏆 Awards response:', result);
        if (result?.success && result?.data) {
          setAwards(result.data);
        }
      } catch (err) {
        console.error('❌ Error fetching awards:', err);
      }
    };
    fetchAwards();
  }, []);

  // Loading state
  if (loading) {
    return (
      <>
        <Header />
        <div className="founder-story-page devnar">
          <div className="loader-container" style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="loader">Loading...</div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Error state
  if (error || !founder) {
    return (
      <>
        <Header />
        <div className="founder-story-page devnar">
          <div className="error-container" style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="error-message">
              <h2>Unable to load founder data</h2>
              <p>Please try again later.</p>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Get first 8 awards
  const displayAwards = Array.isArray(awards) ? awards.slice(0, 8) : [];
  const hasBooks = books.length > 0;

  return (
    <>
      <Header />
      <div className="founder-story-page devnar">
        <a className="skip-link" href="#main-founder">Skip to main content</a>

        {/* Page Hero */}
        <section className="page-hero-founder">
          <div className="hero-background">
            <div className="hero-overlay"></div>
            <div className="hero-pattern"></div>
          </div>
          <div className="wrap hero-content-founder">
            <Reveal>
              <span className="hero-badge">Our Founder</span>
              <h1>Founder Story</h1>
              <p className="hero-subtitle">
                {founder.title_en || 'Dr. A. Saibaba Goud'} — the visionary behind Devnar Foundation, 
                whose passion to serve the needy has transformed thousands of lives.
              </p>
            </Reveal>
          </div>
        </section>

        <main id="main-founder" className="wrap page-content-founder">

          {/* Founder Profile */}
          <section className="founder-profile">
            <Reveal className="section-title" as="div">
              <span className="eyebrow">The Visionary</span>
              <h2>{founder.title_en || 'Dr. A. Saibaba Goud'}</h2>
              <div className="rule" />
            </Reveal>

            <div className="profile-grid">
              <Reveal className="profile-image" as="div" delay={0}>
                <div className="image-wrapper">
                  <img 
                    src={founder.image || '/default-avatar.jpg'} 
                    alt={founder.title_en || 'Founder'} 
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/default-avatar.jpg';
                    }}
                  />
                  <div className="image-overlay">
                    <span className="founder-tag">Founder</span>
                    <span className="founder-tag padma">Padma Shri</span>
                  </div>
                </div>
                <div className="profile-quick-info">
                  <div className="quick-info-item">
                    <span className="qi-label">Credentials</span>
                    <span className="qi-value">M.S., Ph.D.</span>
                  </div>
                  <div className="quick-info-item">
                    <span className="qi-label">Award</span>
                    <span className="qi-value">Padma Shri 2009</span>
                  </div>
                  <div className="quick-info-item">
                    <span className="qi-label">Founded</span>
                    <span className="qi-value">1991</span>
                  </div>
                </div>
              </Reveal>

              <Reveal className="profile-content" as="div" delay={80}>
                <SafeHTML html={founder.description_en} />
                
                <div className="profile-quote">
                  <span className="quote-icon">❝</span>
                  <blockquote>
                    The passion to serve the needy — this is what drives me every single day.
                  </blockquote>
                  <cite>— {founder.title_en || 'Dr. A. Saibaba Goud'}</cite>
                </div>
              </Reveal>
            </div>
          </section>

          {/* Professional Study & Work */}
          <section className="professional-background">
            <Reveal className="section-title" as="div">
              <span className="eyebrow">Professional Journey</span>
              <h2>Professional Study & Work</h2>
              <div className="rule" />
            </Reveal>

            <div className="background-grid">
              <Reveal className="background-card" delay={0}>
                <i className="fas fa-graduation-cap bg-icon"></i>
                <h3>Education</h3>
                <ul>
                  <li>M.S. Degree in Ophthalmology from Osmania University</li>
                  <li>Ph.D. in Ophthalmology from Osmania University</li>
                  <li>First-ever recipient of a Doctorate Degree in Community Ophthalmology in India</li>
                </ul>
              </Reveal>

              <Reveal className="background-card" delay={80}>
                <i className="fas fa-hospital bg-icon"></i>
                <h3>Professional Roles</h3>
                <ul>
                  <li>Former Medical Superintendent, Sarojini Devi Eye Hospital, Hyderabad</li>
                  <li>Former Professor and Head, Department of Ophthalmology, Osmania Medical College, Hyderabad</li>
                  <li>Founder Chairman and Managing Trustee, Devnar Foundation for the Blind</li>
                </ul>
              </Reveal>

              <Reveal className="background-card" delay={160}>
                <i className="fas fa-globe bg-icon"></i>
                <h3>Global Presence</h3>
                <ul>
                  <li>Presented papers at National & International Fora</li>
                  <li>New Delhi, Nairobi, Germany, Beijing, New York, Melbourne</li>
                  <li>Research on blindness in children, dry eyes, diabetes, and eye care</li>
                </ul>
              </Reveal>
            </div>
          </section>

          {/* Books & Publications - FIXED: Properly parsed from JSON */}
          <section className="publications">
            <Reveal className="section-title" as="div">
              <span className="eyebrow">Literary Works</span>
              <h2>Books & Publications</h2>
              <div className="rule" />
            </Reveal>

            {hasBooks ? (
              <div className="publications-grid">
                {books.map((book, index) => (
                  <Reveal key={index} className="publication-card" delay={index * 50}>
                    <i className="fas fa-book pub-icon"></i>
                    <div className="pub-content">
                      <h4>{book.title_en || book.title || 'Untitled'}</h4>
                      {book.description_en && (
                        <p className="pub-description">{book.description_en}</p>
                      )}
                      
                    </div>
                  </Reveal>
                ))}
              </div>
            ) : (
              <div className="publications-empty">
                <p>No books or publications available.</p>
              </div>
            )}
          </section>

          {/* Awards & Recognitions */}
          <section className="awards-recognition">
            <Reveal className="section-title" as="div">
              <span className="eyebrow">Honours</span>
              <h2>Awards & Recognitions</h2>
              <div className="rule" />
              <p className="section-subtitle">
                Dr. Goud has been honoured by Six successive Presidents of India, Governors of different states, 
                and numerous other dignitaries for his exceptional service to society.
              </p>
            </Reveal>

            <div className="awards-list">
              <Reveal className="award-item" delay={0}>
                <i className="fas fa-award award-check"></i>
                <span className="award-text">Padma Shri Award - 2009</span>
              </Reveal>
              <Reveal className="award-item" delay={40}>
                <i className="fas fa-award award-check"></i>
                <span className="award-text">Dr. B.C. Roy National Award</span>
              </Reveal>
              
              {displayAwards.length > 0 && displayAwards.map((award, index) => (
                <Reveal key={award.id || index} className="award-item" delay={(index + 2) * 40}>
                  <i className="fas fa-trophy award-check"></i>
                  <span className="award-text">{award.title_en || award.title || 'Award'}</span>
                </Reveal>
              ))}
              
              <Reveal className="award-item view-all-awards" delay={(displayAwards.length + 2) * 40}>
                <Link to="/awards" className="btn btn-primary">
                  <i className="fas fa-arrow-right"></i>
                  View All Awards
                </Link>
              </Reveal>
            </div>
          </section>

          {/* CTA Section */}
          <Reveal as="section" className="cta-band-founder">
            <div className="cta-content">
              <span className="cta-eyebrow">Carry Forward the Vision</span>
              <h2>Be a Part of Dr. Goud's Legacy</h2>
              <p>
                Join us in continuing the mission of empowering visually challenged children 
                with education, dignity, and the confidence to become contributing citizens of India.
              </p>
              <div className="cta-buttons">
                <Link className="btn btn-primary" to="/donate">
                  <i className="fas fa-heart"></i> Support the Mission
                </Link>
                <Link className="btn btn-outline btn-outline-light" to="/volunteer">
                  <i className="fas fa-hands-helping"></i> Become a Volunteer
                </Link>
              </div>
            </div>
          </Reveal>

        </main>
      </div>
      <Footer />
    </>
  );
}